import { axiosDefaultInstance } from '@/http/default'

import type Customer from '@/model/Customer'
import type { CustomerBulkCreateRs, CustomerRequest } from '@/model/Customer'
import { CustomerType } from '@/model/Customer'
import type Page from '@/model/Page'
import { useProfileStore } from '@/store/profile.module'
import type { AxiosInstance } from 'axios'
import handleError from './util/ErrorHandler'
import type { ServiceError } from './util/ServiceError'

class CustomerService {
  private axiosInstance: AxiosInstance
  private storeProfile: any

  constructor() {
    this.axiosInstance = axiosDefaultInstance
    // this.storeProfile = useProfileStore()
  }

  async findAll(): Promise<Customer[] | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Customer[]>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findAllV2(
    pageNumber?: number,
    pageSize?: number,
    name?: string,
    vatNumber?: string,
    email?: string,
    phone?: string,
    sort?: string,
    direction?: string
  ): Promise<Page<Customer> | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []

    if (pageNumber !== undefined) {
      params.push(['pageNumber', String(pageNumber)])
    }

    if (pageSize !== undefined) {
      params.push(['pageSize', String(pageSize)])
    }

    if (name) {
      params.push(['name', name])
    }

    if (vatNumber) {
      params.push(['vatNumber', vatNumber])
    }

    if (email) {
      params.push(['email', email])
    }

    if (phone) {
      params.push(['phone', phone])
    }

    if (sort) {
      params.push(['sort', sort])
    }

    if (direction) {
      params.push(['direction', direction])
    }

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Page<Customer>>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer/v2`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadAllCsv(
    name?: string,
    vatNumber?: string,
    email?: string,
    phone?: string,
    sort?: string,
    direction?: string
  ): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const params: string[][] = []

    if (name) {
      params.push(['name', name])
    }
    if (vatNumber) {
      params.push(['vatNumber', vatNumber])
    }
    if (email) {
      params.push(['email', email])
    }
    if (phone) {
      params.push(['phone', phone])
    }
    if (sort) {
      params.push(['sort', sort])
    }
    if (direction) {
      params.push(['direction', direction])
    }
    params.push(['locale', locale])

    const searchParams = new URLSearchParams(params)
    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer/csv`,
        { params: searchParams, responseType: 'blob' }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findById(id: string): Promise<Customer | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Customer>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer/${id}`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async createCustomer(customer: CustomerRequest): Promise<Customer | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.post<Customer>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer`,
        customer
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async importCsv(
    file: File,
    columnMapping: { columnIndex: number; field: string }[],
    delimiter: string,
    defaultTypes: CustomerType[] = []
  ): Promise<CustomerBulkCreateRs | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('columnMapping', JSON.stringify({
        columns: columnMapping,
        delimiter,
        ...(defaultTypes.length > 0 ? { defaultTypes } : {}),
      }))

      const networkResponse = await this.axiosInstance.post<CustomerBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer/imports/csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateCustomer(customer: CustomerRequest): Promise<Customer | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.put<Customer>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer/${customer.id}`,
        customer
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async deleteCustomer(id: string): Promise<Customer | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.delete<Customer>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/customer/${id}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }
}

export const customerService = new CustomerService()
